import apiClient from './apiClient';

/**
 * Google Calendar API Service
 * Handles frontend communication with Google Calendar integration endpoints
 */

const googleCalendarService = {
  /**
   * Get Google Calendar authorization URL
   * @param {string} groupId - Group ID
   * @returns {Promise<string>} - Authorization URL
   */
  getAuthUrl: async (groupId) => {
    try {
      const response = await apiClient.get(`/google-calendar/auth`, {
        params: { groupId }
      });
      return response.data.authUrl;
    } catch (error) {
      console.error('Error getting auth URL:', error);
      throw error;
    }
  },

  /**
   * Check if Google Calendar is connected for this group
   * @param {string} groupId - Group ID
   * @returns {Promise<{connected: boolean, accountEmail: string|null}>} - Connection status and account email
   */
  checkConnectionStatus: async (groupId) => {
    try {
      const response = await apiClient.get(`/google-calendar/status`, {
        params: { groupId }
      });
      return {
        connected: response.data.connected,
        accountEmail: response.data.accountEmail
      };
    } catch (error) {
      console.error('Error checking connection status:', error);
      throw error;
    }
  },

  /**
   * Fetch events from Google Calendar
   * @param {string} groupId - Group ID
   * @param {Object} options - Fetch options (calendarId, timeMin, timeMax, maxResults)
   * @returns {Promise<Array>} - Array of calendar events
   */
  fetchEvents: async (groupId, options = {}) => {
    try {
      const response = await apiClient.get(`/google-calendar/events`, {
        params: {
          groupId,
          ...options
        }
      });
      return response.data.events;
    } catch (error) {
      console.error('Error fetching Google Calendar events:', error);
      throw error;
    }
  },

  /**
   * List available calendars
   * @param {string} groupId - Group ID
   * @returns {Promise<Array>} - Array of available calendars
   */
  listCalendars: async (groupId) => {
    try {
      const response = await apiClient.get(`/google-calendar/calendars`, {
        params: { groupId }
      });
      return response.data.calendars;
    } catch (error) {
      console.error('Error listing calendars:', error);
      throw error;
    }
  },

  /**
   * Import selected events into database
   * @param {string} groupId - Group ID
   * @param {Array} events - Array of events to import
   * @returns {Promise<Object>} - Import results
   */
  importEvents: async (groupId, events) => {
    try {
      const response = await apiClient.post(`/google-calendar/import`, {
        groupId,
        events
      });
      return response.data;
    } catch (error) {
      console.error('Error importing events:', error);
      throw error;
    }
  },

  /**
   * Disconnect Google Calendar
   * @param {string} groupId - Group ID
   * @returns {Promise<Object>} - Success message
   */
  disconnect: async (groupId) => {
    try {
      const response = await apiClient.post(`/google-calendar/disconnect`, {
        groupId
      });
      return response.data;
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      throw error;
    }
  }
};

export default googleCalendarService;
